import { DestroyRef, Directive, inject, input, OnInit } from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ShellStore } from '@business/state';
import { TTerminateAppOptions } from '@business/types';
import { BaseButtonComponent } from '@business/ui';
import { normalizeDirectiveInput } from '@business/utils';
import { map, tap } from 'rxjs';
import { OpportunityService } from '../opportunity';

/**
 * @description
 * Handle the application's exit behavior, placed on a button
 * this directive will subscribe to its host button's "buttonClick" event
 * and implement the default or specific exit behavior according to the domain's settings;
 *
 * By default - we navigate out of the app, this behavior can be changed to show a confirmation dialog before redirecting,
 * to do that we can pass a boolean indication to "terminateApplication" method
 */

@Directive({
  selector: '[exitApplication]'
})
export class ExitApplicationDirective implements OnInit {
  #shellStore = inject(ShellStore);
  #destroyRef = inject(DestroyRef);
  #button = inject(BaseButtonComponent);
  #opportunityService = inject(OpportunityService);

  readonly exitApplication = input<TTerminateAppOptions | '' | undefined>();

  /** get a reference to the owner button's click emitter */
  #buttonClick = outputToObservable(this.#button.buttonClick);

  ngOnInit(): void {
    /** subscribe to the owner button's click event */
    this.#buttonClick
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map(() => this.#shellStore.domainSettings()),
        tap(({ confirmBeforeExit }) => {
          const exitParams = normalizeDirectiveInput(this.exitApplication());

          this.#opportunityService.terminateOpportunity({
            shouldConfirm: exitParams?.shouldConfirm ?? confirmBeforeExit
          });
        })
      )
      .subscribe();
  }
}
